// The mock preview's instance: what the platform's client core (the real one, @hq-org/platform-wasm) talks to instead of a
// truss, so the preview's board and chat are the cores' views and intents and no rule of theirs is copied here. It
// speaks the wire the core speaks (truss:Interface/sync and graphql): GET /graphql/kinds (the real engine's answer,
// tools/mock-kinds.ts), GET /sync ranges of a log of events (the fixture as creations, records.ts; `from=` a connected
// instance's log), and the GraphQL documents the kit sends (KitConnections, KitMe, KitSession, KitWrite/KitWrites). A write is
// stored as it is asked and appended to the log; nothing is checked: the preview only shows.
import type { MockLog, MockEvent } from "./records";

type Rec = { kind: string; id: string; space: string | null; fields: Record<string, unknown>; createdAt: string; updatedAt: string; createdBy: string; updatedBy: string };
type Kind = { full: string; gql: string; patch: string; prefix: string; scalars: string[]; single: string[]; many: string[] };
type Json = Record<string, any>;

const json = (body: unknown, status = 200) => new Response(JSON.stringify(body), { status, headers: { "content-type": "application/json" } });

export type MockInstance = { fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> };

/** `logs`: the home's log under "" and each connected instance's under its name; `me`: the signed-in person;
 *  `spaces`: the home's spaces they read in full (KitSession; the other spaces in the log are stub ancestors). */
export function mockInstance(o: { logs: Record<string, MockLog>; me: string; spaces: string[]; kinds: () => Promise<Kind[]> }): MockInstance {
	const logs = o.logs;
	const records = new Map<string, Rec>();
	const fold = (e: MockEvent) => {
		const r = records.get(e.id) ?? { kind: e.kind, id: e.id, space: e.space, fields: {}, createdAt: e.at, updatedAt: e.at, createdBy: e.by, updatedBy: e.by };
		for (const c of e.fields) {
			const have = r.fields[c.field];
			if (Array.isArray(have) && !Array.isArray(c.to)) {
				const xs = have.filter((x) => c.from == null || JSON.stringify(x) !== JSON.stringify(c.from));
				if (c.to != null) xs.push(c.to);
				r.fields[c.field] = xs;
			} else if (c.many) r.fields[c.field] = c.to == null ? [] : [c.to];
			else r.fields[c.field] = c.to;
		}
		r.updatedAt = e.at;
		r.updatedBy = e.by;
		records.set(e.id, r);
	};
	for (const log of Object.values(logs)) for (const e of log.events) fold(e);

	const logOf = (id: string) => logs[id.includes("/") ? id.split("/")[0]! : ""] ?? logs[""]!;
	const node = (k: Kind, r: Rec) => {
		const out: Json = {
			id: r.id,
			address: r.id,
			kind: r.kind,
			space: r.space ? { id: r.space } : null,
			createdAt: r.createdAt,
			updatedAt: r.updatedAt,
			createdBy: { id: r.createdBy },
			updatedBy: { id: r.updatedBy },
			links: (r.fields.links as unknown[]) ?? [],
		};
		for (const f of k.scalars) out[f] = r.fields[f] ?? null;
		for (const f of k.single) out[f] = typeof r.fields[f] === "string" ? { id: r.fields[f] } : null;
		for (const f of k.many) out[f] = { edges: ((r.fields[f] as string[]) ?? []).map((id) => ({ address: id, node: { id } })) };
		return out;
	};

	/** One create or update as the kit asks it: the patch stored, one event appended to the record's log. */
	const write = (kinds: Kind[], op: string, patch: Json, id: string, tx: string) => {
		const [key, fields] = Object.entries(patch)[0] as [string, Json];
		const k = kinds.find((x) => x.patch === key);
		if (!k) throw new Error(`unknown patch ${key}`);
		const space = typeof fields.space === "string" ? fields.space : null;
		const full = op === "create" && space?.includes("/") && !id.includes("/") ? `${space.split("/")[0]}/${id}` : id;
		const had = records.get(full);
		if (op === "update" && !had) throw new Error(`not found: ${full}`);
		const log = logOf(full);
		const at = new Date().toISOString();
		const changes: MockEvent["fields"] = [];
		for (const [f, v] of Object.entries(fields)) {
			if (f === "space") continue;
			const many = k.many.includes(f) || f === "links";
			if (many && v && typeof v === "object" && !Array.isArray(v)) {
				// {add, remove, note}: one change per item, as the log has it
				for (const x of (v.remove as unknown[]) ?? []) changes.push({ field: f, from: (x as Json)?.id ?? x, to: null, many });
				for (const x of (v.add as unknown[]) ?? []) changes.push({ field: f, from: null, to: (x as Json)?.id && f !== "links" ? (x as Json).id : x, note: v.note ?? (x as Json)?.note ?? undefined, many });
			} else if (many && Array.isArray(v)) {
				for (const x of (had?.fields[f] as unknown[]) ?? []) changes.push({ field: f, from: x, to: null, many });
				for (const x of v) changes.push({ field: f, from: null, to: x, many });
			} else changes.push({ field: f, from: had?.fields[f] ?? null, to: v ?? null });
		}
		const e: MockEvent = { seq: ++log.head, tx, at, by: o.me, kind: k.full, id: full, space: had?.space ?? space, fields: changes };
		log.events.push(e);
		fold(e);
		return node(k, records.get(full)!);
	};

	async function graphql(body: Json): Promise<Response> {
		const q = String(body.query ?? "");
		const v = (body.variables ?? {}) as Json;
		const head = logs[""]!.head;
		if (q.includes("KitConnections"))
			return json({ data: { connections: { nodes: Object.keys(logs).filter(Boolean).map((instance) => ({ instance })) } }, extensions: { head } });
		if (q.includes("KitSession")) {
			const r = records.get(o.me);
			const spaces = o.spaces.map((id) => ({ space: { id }, permissions: ["*"] }));
			return json({ data: { session: { person: { id: o.me, name: r?.fields.name ?? o.me }, credential: "mock", via: null, permissions: ["*"], spaces } }, extensions: { head } });
		}
		if (q.includes("KitMe")) {
			const r = records.get(o.me);
			return json({ data: { me: { id: o.me, name: r?.fields.name ?? o.me, type: r?.fields.type ?? "human" } }, extensions: { head } });
		}
		if (q.includes("KitWrite")) {
			const kinds = await o.kinds();
			const data: Json = {};
			const errors: Json[] = [];
			const calls = [...q.matchAll(/(?:(w\d+): )?(create|update)\(/g)];
			for (const [i, m] of calls.entries()) {
				const alias = m[1] ?? m[2]!;
				const sfx = m[1] ? String(i) : "";
				try {
					data[alias] = write(kinds, m[2]!, v[m[1] ? `p${sfx}` : "patch"], v[`id${sfx}`], v[`tx${sfx}`]);
				} catch (e) {
					errors.push({ message: String((e as Error).message), path: [alias], extensions: { code: "NOT_FOUND" } });
				}
			}
			return json({ data, ...(errors.length ? { errors } : {}), extensions: { head: logs[""]!.head } });
		}
		return json({ errors: [{ message: "the mock preview answers this from sample data elsewhere", extensions: { code: "UNAVAILABLE" } }] });
	}

	return {
		async fetch(input, init) {
			const url = new URL(typeof input === "string" ? input : input instanceof URL ? input.href : input.url);
			if (url.pathname === "/graphql/kinds") return json(await o.kinds());
			if (url.pathname === "/sync") {
				const log = logs[url.searchParams.get("from") ?? ""];
				if (!log) return json({ error: "unknown instance" }, 404);
				const since = Number(url.searchParams.get("since") ?? 0);
				const until = Number(url.searchParams.get("until") ?? log.head);
				const limit = Number(url.searchParams.get("limit") ?? 500);
				const all = log.events.filter((e) => e.seq > since && e.seq <= until);
				return json({ v: 1, head: log.head, events: all.slice(0, limit).map(({ fields, ...e }) => ({ ...e, fields: fields.map(({ many: _, ...c }) => c) })), more: all.length > limit });
			}
			if (url.pathname === "/graphql" && init?.method === "POST") return graphql(JSON.parse(String(init.body ?? "{}")));
			return json({ error: "not in the mock preview" }, 404);
		},
	};
}
